import React, { useState, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations/translations';
import { getDatabase, ref, push, serverTimestamp } from 'firebase/database';
import Footer from './Footer';
import LanguageSwitch from './LanguageSwitch';
import './LeadCapture.css';

const LeadCapture = () => {
  const { language } = useLanguage();
  const t = translations[language].leadCapture;
  const [formData, setFormData] = useState({ name: '', email: '', company: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const formRef = useRef(null);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) return;

    setIsSubmitting(true);
    try {
      const db = getDatabase();
      const leadsRef = ref(db, 'leads');
      await push(leadsRef, {
        ...formData,
        timestamp: serverTimestamp(),
        source: 'lead-capture',
      });
      setIsSubmitted(true);
    } catch (err) {
      console.error('Lead capture error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="lc-wrapper">
      {/* Minimal Nav */}
      <nav className="lc-nav">
        <div className="lc-nav-inner">
          <div className="lc-nav-logo-wrap">
            <img src="../img/bonusclub_logo.webp" alt="BonusClub" className="lc-nav-logo" />
            <span className="lc-nav-ai-tag">AI</span>
          </div>
          <LanguageSwitch />
          <button className="lc-nav-cta" onClick={scrollToForm}>
            {t.navCta}
          </button>
        </div>
      </nav>

      {/* Hero: Video + Signup */}
      <section className="lc-hero">
        <div className="lc-hero-glow lc-hero-glow-1" />
        <div className="lc-hero-glow lc-hero-glow-2" />

        <div className="lc-hero-inner">
          {/* Left: Video */}
          <div className="lc-hero-video-col">
            <span className="lc-hero-badge">{t.badge}</span>
            <h1 className="lc-hero-title">{t.title}</h1>
            <p className="lc-hero-subtitle">{t.subtitle}</p>

            <div className="lc-video-wrap">
              <div className="lc-video-container">
                <div className="lc-video-placeholder">
                  <div className="lc-play-btn" onClick={() => {
                    const iframe = document.querySelector('.lc-video-iframe');
                    if (iframe) {
                      iframe.style.display = 'block';
                      iframe.src = iframe.dataset.src + '&autoplay=1';
                      document.querySelector('.lc-play-btn').style.display = 'none';
                      document.querySelector('.lc-video-overlay').style.display = 'none';
                    }
                  }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                  <div className="lc-video-overlay" />
                  <iframe
                    className="lc-video-iframe"
                    data-src="https://www.youtube.com/embed/YOUR_VIDEO_ID?rel=0&modestbranding=1"
                    title="BonusClub Demo"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{ display: 'none' }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right: Signup Form */}
          <div className="lc-hero-form-col" ref={formRef}>
            <div className="lc-form-card">
              <div className="lc-form-glow" />

              {!isSubmitted ? (
                <>
                  <div className="lc-form-header">
                    <span className="lc-form-eyebrow">{t.formEyebrow}</span>
                    <h2 className="lc-form-title">{t.formTitle}</h2>
                    <p className="lc-form-desc">{t.formDesc}</p>
                  </div>

                  <form onSubmit={handleSubmit} className="lc-form">
                    <div className="lc-input-group">
                      <label className="lc-label">{t.nameLabel}</label>
                      <input
                        type="text"
                        className="lc-input"
                        placeholder={t.namePlaceholder}
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="lc-input-group">
                      <label className="lc-label">{t.emailLabel}</label>
                      <input
                        type="email"
                        className="lc-input"
                        placeholder={t.emailPlaceholder}
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                      />
                    </div>
                    <div className="lc-input-group">
                      <label className="lc-label">{t.companyLabel}</label>
                      <input
                        type="text"
                        className="lc-input"
                        placeholder={t.companyPlaceholder}
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      />
                    </div>

                    <button
                      type="submit"
                      className="lc-submit-btn"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <span className="lc-spinner" />
                      ) : (
                        <>
                          {t.submitBtn}
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M5 12h14M12 5l7 7-7 7" />
                          </svg>
                        </>
                      )}
                    </button>

                    <p className="lc-form-note">{t.formNote}</p>
                  </form>

                  <div className="lc-trust-row">
                    {t.trustItems.map((item, i) => (
                      <div key={i} className="lc-trust-item">
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3.5 8.5L6.5 11.5L12.5 4.5" />
                        </svg>
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="lc-success">
                  <div className="lc-success-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  </div>
                  <h3 className="lc-success-title">{t.successTitle}</h3>
                  <p className="lc-success-desc">{t.successDesc}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Strip */}
      <section className="lc-proof">
        <div className="lc-proof-inner">
          {t.proofStats.map((stat, i) => (
            <div key={i} className="lc-proof-stat">
              <span className="lc-proof-number">{stat.number}</span>
              <span className="lc-proof-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="lc-faq">
        <div className="lc-faq-inner">
          <h2 className="lc-faq-title">{t.faqTitle}</h2>
          <div className="lc-faq-list">
            {t.faqs.map((faq, i) => (
              <div
                key={i}
                className={`lc-faq-item ${openFaq === i ? 'lc-faq-open' : ''}`}
              >
                <button
                  className="lc-faq-question"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span>{faq.q}</span>
                  <svg
                    className={`lc-faq-chevron ${openFaq === i ? 'lc-faq-chevron-open' : ''}`}
                    width="16" height="16" viewBox="0 0 16 16"
                    fill="none" stroke="currentColor" strokeWidth="1.5"
                    strokeLinecap="round" strokeLinejoin="round"
                  >
                    <path d="M4 6l4 4 4-4" />
                  </svg>
                </button>
                <div className="lc-faq-answer">
                  <p>{faq.a}</p>
                </div>
              </div>
            ))}
          </div>

          <button className="lc-faq-cta" onClick={scrollToForm}>
            {t.faqCta}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M7 17L17 7M17 7H7M17 7v10" />
            </svg>
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LeadCapture;
